import { DevTool } from '@hookform/devtools'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { StandardSchemaV1 } from '@standard-schema/spec'
import { KeyboardEvent, useEffect } from 'react'
import { FieldValues, FormProvider, Resolver, useForm } from 'react-hook-form'

import Loading from 'components/Loading'
import { LeaveDialog } from './components/LeaveDialog'
import { FormProps } from './types'

export const Form = <T extends FieldValues>({
  children,
  devTools,
  externalErrors,
  id,
  loading,
  onSubmit,
  onTouched,
  onValid,
  schema,
  schemaDefaults,
  withLeaveDialog = true,
}: FormProps<T>) => {
  const methods = useForm<T>({
    defaultValues: schemaDefaults,
    errors: externalErrors,
    mode: 'onChange',
    resolver: standardSchemaResolver(schema as StandardSchemaV1<T, FieldValues>) as Resolver<
      T,
      FieldValues
    >,
  })

  const {
    control,
    formState: { isDirty, isSubmitSuccessful, isSubmitted, isSubmitting, isValid },
    getValues,
    handleSubmit,
    reset,
  } = methods

  // Pass `touched` state for use outside the form
  useEffect(() => {
    if (onTouched !== undefined) {
      onTouched(isDirty)
    }
  }, [isDirty])

  // Pass `valid` state for use outside the form
  useEffect(() => {
    if (onValid !== undefined) {
      onValid(isValid)
    }
  }, [isValid])

  // Reset `isDirty` state after successful submit
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(getValues(), {
        keepErrors: true,
      })
    }
  }, [isSubmitSuccessful])

  return (
    <>
      {withLeaveDialog && <LeaveDialog {...{ isDirty, isSubmitted, isSubmitting }} />}

      <FormProvider {...methods}>
        {loading ? (
          <Loading />
        ) : (
          <form
            id={id}
            onKeyDown={(e: KeyboardEvent<HTMLFormElement>) => {
              if (e.key === 'Enter') {
                if (
                  (e.target as HTMLElement).closest('.monaco-editor') ||
                  (e.target as HTMLElement).closest('textarea')
                ) {
                  return
                }

                e.preventDefault()
              }
            }}
            onSubmit={onSubmit && handleSubmit(onSubmit)}
          >
            {children(methods)}
          </form>
        )}

        {devTools && <DevTool control={control} />}
      </FormProvider>
    </>
  )
}
